import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import App from './App.vue'
import router from './router'
import './assets/styles/main.css'
import { initializeLearningTracking } from './utils/learningTracker'

const STALE_ASSET_RELOAD_PREFIX = 'stale-asset-reload:'

function normalizePerformanceMethods() {
  if (typeof window === 'undefined' || !window.performance) {
    return
  }

  try {
    const perf = window.performance as Performance & Record<string, any>

    if (typeof perf.clearMarks !== 'function') {
      perf.clearMarks = () => {}
    }

    if (typeof perf.clearMeasures !== 'function') {
      perf.clearMeasures = () => {}
    }
  } catch {
    // Ignore read-only browser implementations and keep app bootstrap resilient.
  }
}

function isStaleAssetError(error: unknown) {
  const message = String((error as any)?.message || error || '')
  return /Unable to preload CSS|Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|Loading CSS chunk/i.test(message)
}

function clearCurrentReloadMarker() {
  if (typeof window === 'undefined') {
    return
  }

  const currentKey = `${STALE_ASSET_RELOAD_PREFIX}${window.location.pathname}`
  window.sessionStorage.removeItem(currentKey)
}

function reloadForStaleAssets(targetPath?: string) {
  if (typeof window === 'undefined') {
    return
  }

  const nextPath = targetPath || `${window.location.pathname}${window.location.search}${window.location.hash}`
  const pathname = nextPath.startsWith('http') ? new URL(nextPath).pathname : nextPath.split('?')[0] || window.location.pathname
  const markerKey = `${STALE_ASSET_RELOAD_PREFIX}${pathname}`

  if (window.sessionStorage.getItem(markerKey)) {
    return
  }

  window.sessionStorage.setItem(markerKey, String(Date.now()))
  const url = new URL(nextPath, window.location.origin)
  url.searchParams.set('_reload', Date.now().toString())
  window.location.assign(url.toString())
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  reloadForStaleAssets(router.currentRoute.value.fullPath)
})

router.onError((error, to) => {
  if (!isStaleAssetError(error)) {
    return
  }

  reloadForStaleAssets(to?.fullPath)
})

normalizePerformanceMethods()

const app = createApp(App)
const pinia = createPinia()

// Register Element Plus icons
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(pinia)
app.use(router)
app.use(ElementPlus, { locale: zhCn })

initializeLearningTracking(router)

void router.isReady().then(() => {
  clearCurrentReloadMarker()
})

app.mount('#app')
